import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Card } from "../components/ui/card";
import { Calculator as Calc } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Calculator() {
    const [principal, setPrincipal] = useState(5000);
    const [rate, setRate] = useState(0);
    const [months, setMonths] = useState(12);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const run = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/calc/payment-plan", {
                principal: Number(principal), interest_rate: Number(rate), months: Number(months),
            });
            setResult(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    return (
        <div className="space-y-8 max-w-3xl" data-testid="calculator-page">
            <div>
                <p className="overline">Tools</p>
                <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">Payment plan calculator</h1>
                <p className="text-slate-600 mt-3 max-w-xl">Amortize a settlement amount into a monthly schedule. Use this to negotiate concrete terms with the creditor.</p>
            </div>

            <Card className="p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <Label htmlFor="p">Settlement amount (USD)</Label>
                        <Input data-testid="calc-principal" id="p" type="number" min="0" value={principal} onChange={(e) => setPrincipal(e.target.value)} className="mt-1.5 h-11 font-mono" />
                    </div>
                    <div>
                        <Label htmlFor="r">Annual interest (%)</Label>
                        <Input data-testid="calc-rate" id="r" type="number" min="0" max="50" step="0.1" value={rate} onChange={(e) => setRate(e.target.value)} className="mt-1.5 h-11 font-mono" />
                    </div>
                    <div>
                        <Label htmlFor="m">Months</Label>
                        <Input data-testid="calc-months" id="m" type="number" min="1" max="120" value={months} onChange={(e) => setMonths(e.target.value)} className="mt-1.5 h-11 font-mono" />
                    </div>
                </div>

                <div>
                    <div className="flex justify-between mb-2">
                        <Label>Term ({months} months)</Label>
                        <span className="text-xs text-slate-500">Drag to adjust</span>
                    </div>
                    <Slider data-testid="calc-slider" value={[Number(months)]} min={3} max={60} step={1} onValueChange={(v) => setMonths(v[0])} />
                </div>

                <Button data-testid="calc-run-btn" onClick={run} disabled={loading} className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-11 px-6">
                    <Calc size={16} className="mr-1.5" /> {loading ? "Calculating…" : "Calculate plan"}
                </Button>
            </Card>

            {result && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden" data-testid="calc-result">
                    <Stat label="Monthly payment" value={`$${result.monthly_payment.toLocaleString()}`} highlight />
                    <Stat label="Total interest" value={`$${result.total_interest.toLocaleString()}`} />
                    <Stat label="Total payment" value={`$${result.total_payment.toLocaleString()}`} />
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, highlight }) {
    return (
        <div className={`p-6 ${highlight ? "bg-[#0F1E3D] text-white" : "bg-white"}`}>
            <p className={`overline ${highlight ? "text-white/60" : ""}`}>{label}</p>
            <p className="font-serif text-3xl mt-2 font-mono">{value}</p>
        </div>
    );
}
